import React from 'react'
import { startCase, toLower } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { SidePanelSummary } from 'components/SidePanel/SidePanelSummary'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { Application, CompanyDetails, CustomerApplicationDetails } from 'types/customers'
import { statusLabelMap } from '../utils/utils'

interface ApplicationSidePanelBodyProps {
  application: Application | undefined
  data: CustomerApplicationDetails | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

interface ApplicationDetailsSectionProps {
  externalReference: string | undefined
  id: string | undefined
  customerType: string | undefined
  onboardingType: string | undefined
  status: Application['status'] | undefined
}

interface ContactDetailsSectionProps {
  documentsIntakeEmail: string | undefined
  userEmail: string | undefined
}

interface CompanyDetailsSectionProps {
  company: CompanyDetails
}

interface BusinessProfileSectionProps {
  company: CompanyDetails
}
const formatEnumValue = (value: string) => startCase(toLower(value))

const ApplicationDetailsSection = ({
  externalReference,
  id,
  customerType,
  onboardingType,
  status,
}: ApplicationDetailsSectionProps) => {
  const { t } = useTranslation()
  return (
    <SidePanelSection heading={t('customers.applicationDetails')}>
      <SidePanelSummary
        rows={[
          {
            label: t('customers.externalReference'),
            content: ellipseMiddleText(externalReference || id || '', 12, 8),
            ...((externalReference || id) && { copyText: externalReference || id }),
          },
          { label: t('customers.customerType'), content: customerType || '' },
          { label: t('customers.onboardingType'), content: onboardingType || '' },
          { label: t('status'), content: (status && statusLabelMap[status]) || status || '' },
        ]}
      />
    </SidePanelSection>
  )
}

const ContactDetailsSection = ({ documentsIntakeEmail, userEmail }: ContactDetailsSectionProps) => {
  const { t } = useTranslation()
  return (
    <SidePanelSection heading={t('customers.contactDetails')}>
      <SidePanelSummary
        rows={[
          ...(documentsIntakeEmail
            ? [
                {
                  label: t('customers.documentsEmail'),
                  content: ellipseMiddleText(documentsIntakeEmail, 12, 8),
                  copyText: documentsIntakeEmail,
                },
              ]
            : []),
          ...(userEmail
            ? [
                {
                  label: t('email'),
                  content: ellipseMiddleText(userEmail, 12, 8),
                  copyText: userEmail,
                },
              ]
            : []),
        ]}
      />
    </SidePanelSection>
  )
}

const CompanyDetailsSection = ({ company }: CompanyDetailsSectionProps) => {
  const { t } = useTranslation()
  return (
    <SidePanelSection heading={t('customers.companyDetails')}>
      <SidePanelSummary
        rows={[
          ...(company.name ? [{ label: t('customers.businessName'), content: company.name }] : []),
          ...(company.entityType
            ? [
                {
                  label: t('customers.entityType'),
                  content: formatEnumValue(company.entityType),
                },
              ]
            : []),
          ...(company.registrationNumber
            ? [
                {
                  label: t('customers.registrationNumber'),
                  content: company.registrationNumber,
                },
              ]
            : []),
          ...(company.taxIdentification?.number
            ? [
                {
                  label: t('customers.taxNumber'),
                  content: company.taxIdentification.number,
                },
              ]
            : []),
          ...(company.taxIdentification?.taxResidenceCountryCode
            ? [
                {
                  label: t('customers.taxResidency'),
                  content: company.taxIdentification.taxResidenceCountryCode,
                },
              ]
            : []),
        ]}
      />
    </SidePanelSection>
  )
}

const BusinessProfileSection = ({ company }: BusinessProfileSectionProps) => {
  const { t } = useTranslation()
  const { businessProfile } = company
  return (
    <SidePanelSection heading={t('customers.businessProfile')}>
      <SidePanelSummary
        rows={[
          ...(businessProfile.naicsCode
            ? [{ label: t('customers.naicsCode'), content: businessProfile.naicsCode }]
            : []),
          ...(businessProfile.monthlyExpectedVolumes
            ? [
                {
                  label: t('customers.monthlyExpectedVolumes'),
                  content: formatEnumValue(businessProfile.monthlyExpectedVolumes),
                },
              ]
            : []),
          ...(businessProfile.sourceOfFunds
            ? [
                {
                  label: t('customers.sourceOfFunds'),
                  content: formatEnumValue(businessProfile.sourceOfFunds),
                },
              ]
            : []),
          ...(businessProfile.intendedUseOfAccount
            ? [
                {
                  label: t('customers.intendedUse'),
                  content: formatEnumValue(businessProfile.intendedUseOfAccount),
                },
              ]
            : []),
        ]}
      />
    </SidePanelSection>
  )
}

export const CustomerSidePanelBody = ({
  application,
  data,
  isLoading,
  isError,
  refetch,
}: ApplicationSidePanelBodyProps) => {
  if (isLoading) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (isError) {
    return (
      <Box paddingX={16} paddingY={32}>
        <RequestErrorStatusCard onReload={refetch} />
      </Box>
    )
  }

  const {
    externalReference,
    id,
    status,
    customerType,
    onboardingType,
    onboardingApplication,
    users,
  } = application || {}

  const userEmail = users?.[0]?.email
  const hasContactDetails = !!onboardingApplication?.documentsIntakeEmail || !!userEmail

  const company = data?.company
  const hasCompanyDetails = !!company
  const hasBusinessProfile = !!company?.businessProfile

  return (
    <Box flexCol gapY={16} padding={16}>
      <ApplicationDetailsSection
        externalReference={externalReference}
        id={id}
        customerType={customerType}
        onboardingType={onboardingType}
        status={status}
      />
      {hasContactDetails && (
        <ContactDetailsSection
          documentsIntakeEmail={onboardingApplication?.documentsIntakeEmail}
          userEmail={userEmail}
        />
      )}
      {hasCompanyDetails && <CompanyDetailsSection company={company} />}
      {hasBusinessProfile && <BusinessProfileSection company={company} />}
    </Box>
  )
}
